import { useState, useEffect, useRef } from 'react'

/**
 * # Composant Carousel
 *
 * Un carrousel d'images flexible et personnalisable développé avec React,
 * utilisant DaisyUI et TailwindCSS pour le style.
 *
 * ## Props
 *
 * @typedef {Object} CarouselProps
 * @property {string[]} [images=[]] - Tableau d'URLs d'images à afficher
 * @property {'standard'|'center'|'end'|'vertical'} [variant='standard'] - Variante de disposition du carrousel
 * @property {boolean} [showNavigation=true] - Afficher les boutons de navigation
 * @property {boolean} [showIndicators=true] - Afficher les indicateurs de position
 * @property {'dots'|'numbers'} [indicatorType='dots'] - Type d'indicateurs
 * @property {'full'|'half'|'third'|'quarter'} [itemWidth='full'] - Largeur des éléments du carrousel
 * @property {string|number} [height='auto'] - Hauteur du carrousel
 * @property {boolean} [autoPlay=false] - Activer le défilement automatique
 * @property {number} [autoPlayInterval=3000] - Intervalle de défilement automatique (ms)
 * @property {number} [animationDuration=300] - Durée de l'animation (ms)
 * @property {string} [backgroundColor=''] - Couleur de fond (classe TailwindCSS)
 * @property {boolean} [spacing=false] - Ajouter de l'espacement entre les éléments
 * @property {boolean} [rounded=true] - Appliquer des coins arrondis
 * @property {string} [className=''] - Classes CSS supplémentaires
 *
 * ## Exemples d'utilisation
 *
 * ### Exemple basique
 * ```jsx
 * import Carousel from 'components/ui/Carrousel'
 *
 * const MyComponent = () => {
 *   const images = [
 *     '/images/slide1.jpg',
 *     '/images/slide2.jpg',
 *     '/images/slide3.jpg'
 *   ]
 *
 *   return (
 *     <Carousel images={images} />
 *   )
 * }
 * ```
 *
 * ### Carrousel avec défilement automatique
 * ```jsx
 * <Carousel
 *   images={images}
 *   autoPlay={true}
 *   autoPlayInterval={5000}
 *   animationDuration={500}
 * />
 * ```
 *
 * ### Carrousel avec éléments multiples visibles
 * ```jsx
 * <Carousel
 *   images={images}
 *   variant="center"
 *   itemWidth="third"
 *   spacing={true}
 *   indicatorType="numbers"
 * />
 * ```
 *
 * ### Carrousel vertical
 * ```jsx
 * <Carousel
 *   images={images}
 *   variant="vertical"
 *   height="400px"
 *   showNavigation={false}
 * />
 * ```
 *
 * ## Fonctionnalités
 *
 * - Navigation manuelle avec boutons précédent/suivant
 * - Navigation par indicateurs (points ou numéros)
 * - Défilement automatique configurable
 * - Plusieurs variantes de disposition (standard, center, end, vertical)
 * - Personnalisation visuelle (largeur, hauteur, coins arrondis, espacement)
 *
 * ## Considérations techniques
 *
 * - Optimisé pour DaisyUI et TailwindCSS
 * - Transitions CSS avec durées configurables
 * - Responsive et adaptable à différentes tailles d'écran
 * - Attributs alt générés automatiquement pour l'accessibilité
 * - Défilement automatique désactivé au démontage du composant
 */

const Carousel = ({
  images = [],
  variant = 'standard', // 'standard', 'center', 'end', 'vertical'
  showNavigation = true,
  showIndicators = true,
  indicatorType = 'dots', // 'dots', 'numbers'
  itemWidth = 'full', // 'full', 'half', 'third', 'quarter'
  height = 'auto',
  autoPlay = false,
  autoPlayInterval = 3000,
  animationDuration = 300,
  backgroundColor = '',
  spacing = false,
  rounded = true,
  className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const carouselRef = useRef(null)
  
  // Gestion du défilement automatique
  useEffect(() => {
    if (!autoPlay) return
    
    const interval = setInterval(() => {
      handleNext()
    }, autoPlayInterval)
    
    return () => clearInterval(interval)
  }, [autoPlay, autoPlayInterval, activeIndex, images.length])
  
  // Fonctions de navigation
  const handlePrev = () => {
    if (isAnimating) return
    
    setIsAnimating(true)
    setActiveIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    )
    
    // Réinitialiser l'état d'animation après la durée de l'animation
    setTimeout(() => {
      setIsAnimating(false)
    }, animationDuration)
  }
  
  const handleNext = () => {
    if (isAnimating) return
    
    setIsAnimating(true)
    setActiveIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    )
    
    // Réinitialiser l'état d'animation après la durée de l'animation
    setTimeout(() => {
      setIsAnimating(false)
    }, animationDuration)
  }
  
  const handleIndicatorClick = (index) => {
    if (isAnimating || index === activeIndex) return
    
    setIsAnimating(true)
    setActiveIndex(index)
    
    // Réinitialiser l'état d'animation après la durée de l'animation
    setTimeout(() => {
      setIsAnimating(false)
    }, animationDuration)
  }
  
  // Déterminer les classes CSS en fonction des props
  const getCarouselClasses = () => {
    let classes = 'carousel'
    
    // Ajouter la classe de variante
    if (variant === 'center') classes += ' carousel-center'
    else if (variant === 'end') classes += ' carousel-end'
    else if (variant === 'vertical') classes += ' carousel-vertical'
    
    // Ajouter la classe d'espacement
    if (spacing) classes += ' p-4 space-x-4'
    
    // Ajouter la classe de coins arrondis
    if (rounded) classes += ' rounded-box'
    
    // Ajouter la classe de couleur de fond
    if (backgroundColor) classes += ` bg-${backgroundColor}`
    
    // Ajouter les classes supplémentaires
    if (className) classes += ` ${className}`
    
    return classes
  }
  
  // Déterminer les classes CSS pour les éléments du carrousel
  const getItemClasses = (index) => {
    let classes = 'carousel-item relative'
    
    // Ajouter la classe de largeur
    if (itemWidth === 'full') classes += ' w-full'
    else if (itemWidth === 'half') classes += ' w-1/2'
    else if (itemWidth === 'third') classes += ' w-1/3'
    else if (itemWidth === 'quarter') classes += ' w-1/4'
    
    // Ajouter la classe de hauteur pour le carrousel vertical
    if (variant === 'vertical') classes += ' h-full'
    
    // Ajouter la classe de coins arrondis pour les éléments
    if (rounded) classes += ' rounded-box'

    if( index !== activeIndex) classes += ' hidden'
   
    return classes
  }
  
  // Déterminer le style de hauteur et d'animation
  const getStyles = () => {
    const styles = {}
    
    // Ajouter la hauteur
    if (height !== 'auto') {
      styles.height = height
    }
    
    // Ajouter la durée de l'animation
    styles.transition = `transform ${animationDuration}ms ease-in-out`
    
    return styles
  }
  
  // Style pour les images
  const getImageStyles = () => {
    const styles = {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      marginBottom: '1rem',
      transition: `opacity ${animationDuration}ms ease-in-out`
    }
    
    if (rounded) {
      styles.borderRadius = 'var(--rounded-box, 1rem)'
    }
    
    return styles
  }
  
  return (
    <div className="relative flex flex-col justify-center">
      {/* Indicateurs */}
      {showIndicators && images.length > 1 && (
        <div className="flex justify-center w-full py-2 gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => handleIndicatorClick(index)}
              className={`
                ${indicatorType === 'dots' 
                  ? 'btn btn-circle btn-xs' 
                  : 'btn btn-xs'}
                ${activeIndex === index 
                  ? 'btn-primary' 
                  : 'btn-ghost'}
                transition-all duration-300
              `}
              disabled={isAnimating}
            >
              {indicatorType === 'dots' ? '' : index + 1}
            </button>
          ))}
        </div>
      )}
      {/* Conteneur du carrousel */}
      <div 
        ref={carouselRef}
        className={getCarouselClasses()} 
        style={getStyles()}
      >
        {/* Éléments du carrousel */}
        {images.map((imageUrl, index) => (
          <div 
            key={index} 
            id={`slide${index}`}
            className={getItemClasses(index)}
          >
            <img 
              src={imageUrl} 
              alt={`Slide ${index + 1}`} 
              style={getImageStyles()}
            />  
          </div>
        ))}
        {/* Boutons de navigation */}
        {showNavigation && (
          <div className="absolute left-5 right-5 top-1/2 flex -translate-y-1/2 transform justify-between">
            <button 
              onClick={handlePrev}
              className="btn btn-circle btn-ghost text-white bg-black/30 hover:bg-black/50 transition-colors duration-300"
              disabled={isAnimating}
            >
              ❮
            </button>
            <button 
              onClick={handleNext}
              className="btn btn-circle btn-ghost text-white bg-black/30 hover:bg-black/50 transition-colors duration-300"
              disabled={isAnimating}
            >
              ❯
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Carousel.propTypes = {
//   images: PropTypes.arrayOf(PropTypes.string).isRequired,
//   variant: PropTypes.oneOf(['standard', 'center', 'end', 'vertical']),
//   showNavigation: PropTypes.bool,
//   showIndicators: PropTypes.bool,
//   indicatorType: PropTypes.oneOf(['dots', 'numbers']),
//   itemWidth: PropTypes.oneOf(['full', 'half', 'third', 'quarter']),
//   height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
//   autoPlay: PropTypes.bool,
//   autoPlayInterval: PropTypes.number,
//   animationDuration: PropTypes.number,
//   backgroundColor: PropTypes.string,
//   spacing: PropTypes.bool,
//   rounded: PropTypes.bool,
//   className: PropTypes.string
// }

export default Carousel